import { t } from "@lingui/macro"
import React, { useCallback } from "react"
import { observer } from "mobx-react"
import { useNavigate } from "react-router-dom"
import { Box, Typography, Grid, Chip, Button } from "@mui/material"

import { AsyncRequestDetailModalStore } from "./store"
import { IAsyncRequestDetailModalProps } from "./types"

import { ModalHeader } from "src/components/ModalHeader"
import { CenteredSpinner } from "src/components/CenteredSpinner"
import { Time } from "src/components/Time"
import { StatusChip } from "src/components/StatusChip"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import { useInitializer } from "src/lib/initializer"
import { parseDate } from "src/lib/date"
import { avy_api_pkg_async_AsyncStatus } from "src/api/models/avy_api_pkg_async_AsyncStatus"
import { GlobalStore } from "src/store"

const Modal = observer(({ id }: IAsyncRequestDetailModalProps) => {
    const store = useStore(AsyncRequestDetailModalStore)
    const globalStore = useStore(GlobalStore)
    const navigate = useNavigate()

    const initialized = useInitializer(async () => {
        await store.init(id)
    }, [id])

    const handleViewItemsClick = useCallback(() => {
        globalStore.modals.pop()
        navigate(`/async-request-items?async_request_id=${id}`)
    }, [globalStore.modals, navigate, id])

    const getStatusChipState = (
        status: avy_api_pkg_async_AsyncStatus,
    ): "positive" | "neutral" | "negative" | "info" | "other" | "dark" => {
        switch (status) {
            case "completed":
                return "positive"
            case "failed":
                return "negative"
            case "processing":
                return "neutral"
            case "partial":
                return "neutral"
            case "pending":
            case "pending_send":
            case "sent":
                return "info"
            default:
                return "other"
        }
    }

    const getStatusText = (status: avy_api_pkg_async_AsyncStatus): string => {
        switch (status) {
            case "created":
                return t`async-requests.status.created`
            case "pending":
                return t`async-requests.status.pending`
            case "pending_send":
                return t`async-requests.status.pending-send`
            case "sent":
                return t`async-requests.status.sent`
            case "processing":
                return t`async-requests.status.processing`
            case "completed":
                return t`async-requests.status.completed`
            case "failed":
                return t`async-requests.status.failed`
            case "partial":
                return t`async-requests.status.partial`
            default:
                return status
        }
    }

    if (!initialized || store.asyncRequest == null) {
        return <CenteredSpinner height="45vh" />
    }

    const asyncRequest = store.asyncRequest

    return (
        <>
            <ModalHeader>
                <Typography variant="h6">
                    {t`async-request-detail.title`} #{asyncRequest.id}
                </Typography>
            </ModalHeader>

            <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-detail.request-id`}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {asyncRequest.request_id ?? "-"}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-detail.status`}
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                            <StatusChip
                                label={getStatusText(
                                    asyncRequest.status ?? "created",
                                )}
                                state={getStatusChipState(
                                    asyncRequest.status ?? "created",
                                )}
                            />
                        </Box>

                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-detail.endpoint`}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {asyncRequest.method} {asyncRequest.endpoint}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-detail.integration-id`}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {asyncRequest.integration_id ?? "-"}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-detail.user-id`}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {asyncRequest.user_id ?? "-"}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-detail.progress`}
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                            <Grid container spacing={1}>
                                <Grid item>
                                    <Chip
                                        label={`${t`async-request-detail.total`}: ${
                                            asyncRequest.total_items ?? 0
                                        }`}
                                        size="small"
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item>
                                    <Chip
                                        label={`${t`async-request-detail.completed`}: ${
                                            asyncRequest.completed_items ?? 0
                                        }`}
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item>
                                    <Chip
                                        label={`${t`async-request-detail.failed`}: ${
                                            asyncRequest.failed_items ?? 0
                                        }`}
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item>
                                    <Chip
                                        label={`${t`async-request-detail.pending`}: ${
                                            asyncRequest.pending_items ?? 0
                                        }`}
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Button
                                onClick={handleViewItemsClick}
                                variant="contained"
                                size="small"
                                color="primary"
                            >
                                {t`async-request-detail.view-items`}
                            </Button>
                        </Box>

                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-detail.created-at`}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {Boolean(asyncRequest.created_at) ? (
                                <Time
                                    date={parseDate(asyncRequest.created_at)}
                                />
                            ) : (
                                "-"
                            )}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-detail.started-at`}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {Boolean(asyncRequest.started_at) ? (
                                <Time
                                    date={parseDate(asyncRequest.started_at)}
                                />
                            ) : (
                                "-"
                            )}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-detail.completed-at`}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {Boolean(asyncRequest.completed_at) ? (
                                <Time
                                    date={parseDate(asyncRequest.completed_at)}
                                />
                            ) : (
                                "-"
                            )}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                            {t`async-request-detail.payload-size`}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {Boolean(asyncRequest.payload_size_bytes)
                                ? `${(
                                      (asyncRequest.payload_size_bytes ?? 0) /
                                      1024
                                  ).toFixed(2)} KB`
                                : "-"}
                        </Typography>
                    </Grid>

                    {Boolean(asyncRequest.error_summary) && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                                {t`async-request-detail.error-summary`}
                            </Typography>
                            <Box
                                sx={{
                                    p: 2,
                                    backgroundColor: "error.light",
                                    borderRadius: 1,
                                    color: "error.contrastText",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    component="pre"
                                    sx={{ whiteSpace: "pre-wrap" }}
                                >
                                    {asyncRequest.error_summary}
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </>
    )
})

export const AsyncRequestDetailModal = (
    props: IAsyncRequestDetailModalProps,
) => (
    <StoreProvider Store={AsyncRequestDetailModalStore}>
        <Modal {...props} />
    </StoreProvider>
)
